import { createRef, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete, MdSync } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import { apiUrlBillingAndInvoicing } from "~constants";
import DialogHelper from "~helpers/DialogHelper";
import { deleteInvoice, syncInvoice } from "~store/billingAndInvoicing/actions";
import { selectCurrentProject } from "~store/user/selectors";

const InvoicesList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = createRef();
  const currentProject = useSelector(selectCurrentProject);

  const actions = useMemo(() => {
    return [
      {
        label: t("common.createNew"),
        icon: MdAdd,
        color: "success",
        link: "/billing-and-invoicing/invoices/form",
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    const handleClickDelete = (row) => async () => {
      if (await DialogHelper.showQuestionDeleteWithFormat(row.invoice_number)) {
        dispatch(deleteInvoice(currentProject.id, row.id)).then(() => {
          tableRef.current.refresh();
        });
      }
    };

    const handleClickSync = (row) => async () => {
      if (
        await DialogHelper.showQuestionYesNo(
          t("question.yesNo.title"),
          t("dashboard.invoices.syncConfirmation").format(row.invoice_number)
        )
      ) {
        dispatch(syncInvoice(row.id)).then(() => {
          tableRef.current.refresh();
        });
      }
    };

    return [
      {
        label: t("dashboard.invoices.invoiceNumber"),
        key: "invoice_number",
      },
      {
        label: t("dashboard.invoices.invoiceCode"),
        key: "invoice_code",
      },
      {
        label: t("dashboard.invoices.invoiceType"),
        key: "invoice_type",
      },
      {
        label: t("dashboard.invoices.invoiceStatus"),
        key: "invoice_status",
      },
      {
        label: t("dashboard.invoices.invoiceDetailStatus"),
        key: "invoice_detail_status",
      },
      {
        label: t("dashboard.invoices.invoiceReceiverInboxTag"),
        key: "invoice_receiver_inbox_tag",
      },
      {
        label: t("dashboard.invoices.invoicePdf"),
        key: "invoice_pdf",
        render: (row) =>
          row.invoice_pdf ? (
            <a href={row.invoice_pdf} rel="noopener noreferrer" target="_blank">
              {t("common.viewPdf")}
            </a>
          ) : null,
      },
      {
        label: t("common.actions"),
        key: "actions",
        actions: true,
        render: (row) => (
          <div className="d-flex justify-content-center gap-1">
            <PRButton
              outline
              color="info"
              icon={MdSync}
              size="sm"
              tooltipText={t("common.sync")}
              onClick={handleClickSync(row)}
            />
            <PRButton
              outline
              color="danger"
              icon={MdDelete}
              size="sm"
              tooltipText={t("common.delete")}
              onClick={handleClickDelete(row)}
            />
          </div>
        ),
      },
    ];
  }, [currentProject.id, dispatch, tableRef, t]);
  return (
    <PRContainer
      actions={actions}
      name={t("layout.sidebar.billingAndInvoicing")}
      parentName={t("layout.sidebar.invoices")}
    >
      <PRTable
        ref={tableRef}
        className="" //
        columns={columns}
        url={apiUrlBillingAndInvoicing.getInvoices.format(currentProject.id)}
      />
    </PRContainer>
  );
};

export default InvoicesList;
